import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';

import { Favorite, FavoriteFilter } from '../models';
import { TagGroup } from '../models/misc.model';
import { SiteV1 } from '../models/site.model';
import { API_URL_PREFIX } from './basic.service';
import { ErrorHandlingService } from './error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  constructor(
    private http: HttpClient,
    private ehs: ErrorHandlingService,
    private alcmeonContext: AlcmeonContextService
  ) {}

  // Fetches the favorites from the server
  getFavorites(): Observable<{ favorites: Favorite[]; teams: any }> {
    const favoritesURL = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/favorites`;
    return this.http.get<{ favorites: Favorite[]; teams: any }>(favoritesURL).pipe(
      tap(res => (res.favorites = res.favorites.filter(fav => fav.active === 1))), // Keep only the active ones
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  // Prepares the favorites in order to be filtered for the specific task
  processAndFilterFavorites(
    favorites: Favorite[],
    site: SiteV1,
    taskTypeInt: number,
    routingTagGroup: TagGroup,
    tagGroups: TagGroup[],
    wantsAllLang: boolean
  ): Favorite[] {
    const filters: FavoriteFilter = {
      channels: [taskTypeInt],
      teams: routingTagGroup ? [routingTagGroup.selectedTagId] : [],
      tagsById: {},
    };

    let isLanguageTagId = null;

    // Build tag filters
    tagGroups
      .filter(tagGroup => tagGroup !== null)
      .forEach(tagGroup => {
        filters.tagsById[tagGroup.id] = {
          isLanguage: tagGroup.isLanguage,
          values: [tagGroup.selectedTagId],
        };

        isLanguageTagId = tagGroup.isLanguage ? tagGroup.id : isLanguageTagId;
      });

    favorites.forEach(favorite => {
      favorite.tagsById = {};
      favorite.tags.forEach(tag => {
        favorite.tagsById[tag.id] = tag.values;
        favorite.languageTagId = isLanguageTagId;
      });
    });

    return this.filterFavorites(favorites, filters, site, wantsAllLang);
  }

  /**
   * Filter the favorites according to task site, site group, channels, teams, tags
   */
  private filterFavorites(
    favorites: Favorite[],
    filters: FavoriteFilter,
    site: SiteV1,
    wantsAllLang: boolean
  ): Favorite[] {
    const { id: siteId, siteGroupId } = site;

    const filteredFavorites = favorites.filter(favorite => {
      const { sites, siteGroupIds } = favorite;

      if (
        ((siteGroupId && siteGroupIds?.length) || (siteId && sites?.length)) &&
        !siteGroupIds?.includes(siteGroupId) &&
        !sites.includes(siteId)
      ) {
        return false;
      }

      for (const filterKey in filters) {
        // if it's not tagsById, it's 'channels' or 'teams', no need to loop on array of tags
        if (filterKey === 'tagsById') {
          for (const tagId in filters.tagsById) {
            if (
              !this.isFavoriteMatchFilter(
                filters.tagsById[tagId].values,
                favorite.tagsById[tagId],
                filters.tagsById[tagId].isLanguage,
                wantsAllLang
              )
            ) {
              return false;
            }
          }
        } else if (!this.isFavoriteMatchFilter(filters[filterKey], favorite[filterKey])) {
          return false;
        }
      }

      return true;
    });

    return filteredFavorites;
  }

  private isFavoriteMatchFilter(
    filterValues: number[],
    favoriteValues: number[],
    isLanguage?: boolean,
    wantsAllLang?: boolean
  ) {
    if (!filterValues?.length || !favoriteValues?.length) {
      return true;
    }

    if (isLanguage && wantsAllLang) {
      return true;
    }

    return filterValues.some(id => favoriteValues.includes(id));
  }
}
